'use client';

import Image from 'next/image';
import React from 'react';
import { FaCalendarAlt, FaClock, FaFacebookF, FaLinkedinIn, FaTwitter, FaUser, FaWhatsapp } from 'react-icons/fa';

import Breadcrumb from '@/components/Breadcrumb';
import Header from '@/components/Header';

import { useLanguage } from '@/contexts/LanguageContext';

export default function HamilelikteBotoks() {
  const { t } = useLanguage();
  const pageUrl = typeof window !== 'undefined' ? window.location.href : 'https://drbariscin.com/tr/blog/hamilelikte-botoks-bilimsel-gercekler-riskler-ve-uzman-gorusu';
  const pageTitle = 'Hamilelikte Botoks: Bilimsel Gerçekler, Riskler ve Uzman Görüşü';
  const pageDescription = 'Hamilelikte botoks yapılır mı? Botulinum toksininin gebelikte güvenliği, olası riskleri, hormonel değişikliklerin etkisi ve uzman önerileri.';

  const handleShare = (platform: string) => {
    let shareUrl = '';
    
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent(pageTitle)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(pageTitle + ' - ' + pageUrl)}`;
        break;
      default:
        return;
    }
    
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 to-purple-100">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Breadcrumb */}
        <div className="mb-8">
          <Breadcrumb
            items={[
              { label: 'Ana Sayfa', href: '/tr' },
              { label: 'Blog', href: '/tr/blog' },
              { label: 'Hamilelikte Botoks', href: '/tr/blog/hamilelikte-botoks-bilimsel-gercekler-riskler-ve-uzman-gorusu' },
            ]}
          />
        </div>

        {/* Article Header */}
        <article className="bg-white rounded-3xl shadow-xl overflow-hidden">
          {/* Featured Image */}
          <div className="relative h-64 sm:h-80">
            <Image
              src="/images/terleme-tedavisi.webp"
              alt="Hamilelikte botoks uygulaması ve güvenlik"
              fill
              className="object-cover"
              priority
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <div className="absolute bottom-6 left-6">
              <span className="bg-pink-600 text-white px-4 py-2 rounded-full text-sm font-medium">
                Hamilelik & Güvenlik
              </span>
            </div>
          </div>

          {/* Article Content */}
          <div className="p-6 sm:p-8">
            {/* Title */}
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Hamilelikte Botoks: Bilimsel Gerçekler, Riskler ve Uzman Görüşü
            </h1>

            {/* Meta Information */}
            <div className="flex flex-wrap items-center gap-6 mb-8 text-sm text-gray-600 border-b border-gray-200 pb-6">
              <div className="flex items-center">
                <FaUser className="w-4 h-4 mr-2" />
                <span>Op. Dr. Barış Çin</span>
              </div>
              <div className="flex items-center">
                <FaCalendarAlt className="w-4 h-4 mr-2" />
                <span>16 Şubat 2026</span>
              </div>
              <div className="flex items-center">
                <FaClock className="w-4 h-4 mr-2" />
                <span>10 dakika okuma</span>
              </div>
            </div>

            {/* Share Buttons */}
            <div className="flex items-center gap-4 mb-8">
              <span className="text-gray-600 font-medium">Paylaş:</span>
              <div className="flex gap-3">
                <button className="p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors" onClick={() => handleShare('facebook')}>
                  <FaFacebookF className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-400 text-white rounded-full hover:bg-blue-500 transition-colors" onClick={() => handleShare('twitter')}>
                  <FaTwitter className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-700 text-white rounded-full hover:bg-blue-800 transition-colors" onClick={() => handleShare('linkedin')}>
                  <FaLinkedinIn className="w-4 h-4" />
                </button>
                <button className="p-2 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors" onClick={() => handleShare('whatsapp')}>
                  <FaWhatsapp className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Article Content */}
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Hamilelik, bir kadının vücudunun en büyük dönüşüm sürecidir.
                Bu dönemde yaşanan hormonal değişiklikler, ciltteki görünümden kas tonusuna, dolaşımdan bağışıklık sistemine kadar her şeyi etkiler.
              </p>

              <p className="text-gray-700 mb-6 leading-relaxed">
                Bazı anne adayları, özellikle yüz bölgesinde belirginleşen mimik çizgileri veya terleme sorunları nedeniyle botoksu bir "geçici rahatlama çözümü" olarak düşünür.
                Ancak tıp dünyasında hamilelikte botoks uygulaması kesin olarak önerilmemektedir.
              </p>

              <div className="bg-pink-50 border-l-4 border-pink-400 p-4 mb-6">
                <p className="text-pink-800 font-medium">
                  💡 Nedeni: botulinum toksininin gebelikteki etkileri üzerine yeterli bilimsel veri bulunmamasıdır.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                1. Botoks Nedir ve Nasıl Etki Eder?
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Botoks, Clostridium botulinum bakterisinden elde edilen nörotoksin türevidir.
                Sinir uçlarında asetilkolin adlı kimyasalın salınımını durdurarak kas kasılmasını geçici olarak engeller.
              </p>

              <div className="bg-blue-50 rounded-xl p-6 mb-6">
                <p className="text-blue-800 mb-4 font-medium">
                  Bu etki sayesinde;
                </p>
                <ul className="list-disc list-inside space-y-2 text-blue-800">
                  <li>kırışıklıklar azalır,</li>
                  <li>migren ağrıları hafifler,</li>
                  <li>aşırı terleme kontrol altına alınır.</li>
                </ul>
                <p className="text-blue-800 mt-4">
                  Ancak aynı etkiyi oluşturan mekanizma, gebelikte bebeğin gelişim sürecinde olası riskler barındırabilir.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                2. Botulinum Toksinin Gebelikteki Durumu
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Şu ana kadar botoksun plasenta bariyerini geçip geçmediğini gösteren doğrudan insan çalışması yoktur.
                Bu eksiklik, hem etik nedenlerle hem de olası risklerden dolayıdır.
              </p>

              <div className="bg-yellow-50 rounded-xl p-6 mb-6">
                <p className="text-yellow-800 mb-4 font-medium">
                  Mevcut veriler:
                </p>
                <ul className="list-disc list-inside space-y-2 text-yellow-800">
                  <li>Hayvan deneylerinde yüksek dozlarda uygulanan botoksun, embriyo gelişiminde büyüme geriliği oluşturabileceği görülmüştür.</li>
                  <li>İnsanlarda bu etki kanıtlanmamıştır, ancak risk ihtimali nedeniyle işlem yapılmaz.</li>
                </ul>
                <p className="text-yellow-800 mt-4 font-medium">
                  Bu nedenle FDA, botoksu Gebelik Kategorisi C olarak sınıflandırır:
                </p>
                <p className="text-yellow-800 italic">
                  "Fetüse zararı kanıtlanmamıştır ancak olasılığı göz ardı edilemez."
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                3. Hamilelikte Botoksun Riskleri
              </h2>
              
              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-pink-50">
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Olası Etki</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Açıklama</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Plasentaya geçiş riski</td>
                      <td className="border border-gray-300 px-4 py-3">Düşük olasılıklı, ancak teorik risk mevcut</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Kas sistemi etkisi</td>
                      <td className="border border-gray-300 px-4 py-3">Anne dolaşımıyla bebeğin kas gelişimini etkileyebilir</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Bağışıklık tepkisi</td>
                      <td className="border border-gray-300 px-4 py-3">Gebelikte immün sistem baskılı olduğu için alerjik yanıt farklı gelişebilir</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Enfeksiyon riski</td>
                      <td className="border border-gray-300 px-4 py-3">Gebelikte yara iyileşmesi yavaşlar, cilt hassasiyeti artar</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Psikolojik stres</td>
                      <td className="border border-gray-300 px-4 py-3">Uygulama sonrası oluşan kaygı hormon dengelerini bozabilir</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
                <p className="text-red-800 font-medium">
                  Dolayısıyla "zarar kanıtlanmadı" demek "güvenlidir" anlamına gelmez.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                4. Uluslararası Sağlık Kuruluşlarının Görüşü
              </h2>
              
              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-purple-50">
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Kurum</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Görüş</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Öneri</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">FDA (ABD)</td>
                      <td className="border border-gray-300 px-4 py-3">Botulinum toksin kategori C</td>
                      <td className="border border-gray-300 px-4 py-3">"Gerekmedikçe uygulanmamalı"</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">WHO</td>
                      <td className="border border-gray-300 px-4 py-3">Klinik veri yetersiz</td>
                      <td className="border border-gray-300 px-4 py-3">"Gebelik boyunca ertelenmeli"</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">American Academy of Dermatology</td>
                      <td className="border border-gray-300 px-4 py-3">Kozmetik amaçla yasak</td>
                      <td className="border border-gray-300 px-4 py-3">"Doğum sonrası değerlendirilmeli"</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Türk Dermatoloji Derneği</td>
                      <td className="border border-gray-300 px-4 py-3">Risk-fayda dengesi yok</td>
                      <td className="border border-gray-300 px-4 py-3">"Hamilelikte botoks yapılmamalı"</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="bg-purple-50 border-l-4 border-purple-400 p-4 mb-6">
                <p className="text-purple-800 font-medium">
                  💬 Özet: Tüm uluslararası kılavuzlar gebelikte botoksu uygun bulmamaktadır.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                5. Estetik İhtiyaç ve Psikolojik Boyut
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Hamilelik döneminde birçok kadın aynadaki görüntüsünü farklı algılar.
                Ödem, hormonal değişiklikler, yorgunluk ve ciltteki matlaşma, estetik müdahale isteğini tetikleyebilir.
              </p>

              <p className="text-gray-700 mb-4 leading-relaxed">
                Ancak bu dönemde yapılacak en doğru yaklaşım, bedeni olduğu haliyle kabullenmek ve estetik işlemleri doğum sonrasına ertelemektir.
              </p>

              <div className="bg-green-50 border-l-4 border-green-400 p-4 mb-6">
                <p className="text-green-800 font-medium">
                  💡 Estetik bakım doğumdan sonra kaldığı yerden güvenle devam eder, ama bebeğin sağlığı telafi edilemez.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                6. Doğal Alternatifler
              </h2>
              
              <div className="bg-indigo-50 rounded-xl p-6 mb-6">
                <p className="text-indigo-800 mb-4 font-medium">
                  Hamilelikte botoks yerine uygulanabilecek, güvenli kabul edilen destek yöntemler:
                </p>
                <ul className="list-disc list-inside space-y-2 text-indigo-800">
                  <li>Hyaluronik asit ve C vitamini serumları (topikal, medikal olmayan)</li>
                  <li>Soğuk kompres ve yüz masajı</li>
                  <li>Bol su tüketimi ve düzenli uyku</li>
                  <li>Hamilelik vitaminleriyle desteklenen cilt bakımı</li>
                  <li>Doğal kolajen içeren beslenme</li>
                </ul>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                7. Doğum Sonrası Dönem
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Botoks doğumdan hemen sonra da önerilmez.
                Emzirme döneminde süt yoluyla geçiş riski henüz kanıtlanmamış olsa da, güvenlik açısından genellikle en az 3 ay bekleme süresi önerilir.
              </p>

              <p className="text-gray-700 mb-6 leading-relaxed">
                Bu süre sonunda hem hormon dengesi oturur hem de cilt yapısı normale döner.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Uzman Görüşü (Dr. Barış Çin)
              </h2>
              
              <div className="bg-blue-50 border-l-4 border-blue-400 p-6 mb-6">
                <p className="text-blue-800 italic">
                  "Hamilelik döneminde her türlü estetik işlem, yalnızca fiziksel değil, hormonal ve psikolojik açıdan da değerlendirilmelidir.
                  Botoks, bu dönemde tıbben gerekli olmadığı sürece yapılmamalıdır.
                  Çünkü güvenli veri eksikliği, riskin olmadığını değil — bilinmediğini gösterir."
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Sık Sorulan Sorular (FAQ)
              </h2>
              
              <div className="space-y-6 mb-8">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">1. Hamileliğimin başında botoks yaptırdım, ne yapmalıyım?</h3>
                  <p className="text-gray-600">Endişelenmeyin; bugüne kadar düşük doz uygulamada olumsuz etki saptanmamıştır. Ancak doktor takibi önerilir.</p>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">2. Hamileliğin son aylarında yapılabilir mi?</h3>
                  <p className="text-gray-600">Hayır, kas gevşetici etkiler doğum sürecini teorik olarak etkileyebilir.</p>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">3. Hamilelikte terleme botoksu yapılabilir mi?</h3>
                  <p className="text-gray-600">Hayır. Estetik veya medikal amaç fark etmez, tüm botoks işlemleri ertelenmelidir.</p>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">4. Doğumdan ne kadar sonra botoks yaptırabilirim?</h3>
                  <p className="text-gray-600">Emzirme bittikten sonra, yani doğumdan yaklaşık 6 ay sonra önerilir.</p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Sonuç
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Hamilelik, anne ve bebek için fiziksel olduğu kadar duygusal bir yolculuktur.
                Bu dönemde en doğru yaklaşım, vücudu doğanın kendi ritminde bırakmak ve medikal işlemleri doğum sonrasına ertelemektir.
              </p>

              <p className="text-gray-700 mb-6 leading-relaxed">
                Botoks, doğum sonrası dönemde güvenle uygulanabilen bir işlemdir; ancak hamilelik boyunca en güvenli botoks, hiç yapılmamış olandır.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Kaynakça
              </h2>
              
              <div className="bg-gray-50 rounded-xl p-6 mb-8">
                <ul className="list-disc list-inside space-y-2 text-gray-700">
                  <li>FDA (2024) – "Botulinum Toxin Type A: Pregnancy Risk Category C"</li>
                  <li>American Academy of Dermatology (AAD) – "Cosmetic Procedures During Pregnancy"</li>
                  <li>World Health Organization (WHO) – "Clinical Recommendations for Non-Surgical Aesthetics"</li>
                  <li>Türk Dermatoloji Derneği (TDD) – "Gebelikte Kozmetik Uygulama Rehberi"</li>
                  <li>Harvard Health Publishing (2024) – "Safety of Injectables in Pregnant Women"</li>
                </ul>
              </div>

              <div className="flex flex-wrap gap-2 mb-8">
                <span className="bg-pink-100 text-pink-800 px-3 py-1 rounded-full text-sm">#botoks</span>
                <span className="bg-pink-100 text-pink-800 px-3 py-1 rounded-full text-sm">#hamilelik</span>
                <span className="bg-pink-100 text-pink-800 px-3 py-1 rounded-full text-sm">#gebelik</span>
                <span className="bg-pink-100 text-pink-800 px-3 py-1 rounded-full text-sm">#anneadayı</span>
                <span className="bg-pink-100 text-pink-800 px-3 py-1 rounded-full text-sm">#estetik</span>
                <span className="bg-pink-100 text-pink-800 px-3 py-1 rounded-full text-sm">#güvenlik</span>
              </div>

              {/* Social Share Buttons */}
              <div className="border-t pt-8 mt-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Bu yazıyı paylaş:</h3>
                <div className="flex space-x-4">
                  <button
                    onClick={() => handleShare('facebook')}
                    className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <FaFacebookF className="w-4 h-4" />
                    <span>Facebook</span>
                  </button>
                  <button
                    onClick={() => handleShare('twitter')}
                    className="flex items-center space-x-2 bg-blue-400 text-white px-4 py-2 rounded-lg hover:bg-blue-500 transition-colors"
                  >
                    <FaTwitter className="w-4 h-4" />
                    <span>Twitter</span>
                  </button>
                  <button
                    onClick={() => handleShare('linkedin')}
                    className="flex items-center space-x-2 bg-blue-700 text-white px-4 py-2 rounded-lg hover:bg-blue-800 transition-colors"
                  >
                    <FaLinkedinIn className="w-4 h-4" />
                    <span>LinkedIn</span>
                  </button>
                  <button
                    onClick={() => handleShare('whatsapp')}
                    className="flex items-center space-x-2 bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition-colors"
                  >
                    <FaWhatsapp className="w-4 h-4" />
                    <span>WhatsApp</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </article>

        {/* CTA Section */}
        <div className="mt-12 bg-gradient-to-r from-pink-600 to-purple-700 rounded-3xl p-8 text-white text-center">
          <h3 className="text-2xl font-bold mb-4">Hamilelik Döneminde Güvenli Estetik Danışmanlığı</h3>
          <p className="mb-6">Uzman doktorumuzdan hamilelik döneminde güvenli estetik seçenekleri hakkında detaylı bilgi alın.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={`https://wa.me/905438999586?text=${encodeURIComponent('Hamilelik döneminde estetik seçenekleri hakkında bilgi almak istiyorum.')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-6 py-3 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors"
            >
              <FaWhatsapp className="w-5 h-5 mr-2" />
              WhatsApp ile İletişim
            </a>
            <a
              href="/tr/iletisim"
              className="inline-flex items-center justify-center px-6 py-3 bg-white text-pink-600 rounded-full hover:bg-gray-100 transition-colors"
            >
              Randevu Al
            </a>
          </div>
        </div>

        {/* Related Articles */}
        <div className="mt-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">İlgili Yazılar</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <a
              href="/tr/blog/botoks-nedir-nasil-yapilir"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Botoks Nedir, Nasıl Yapılır?</h4>
              <p className="text-gray-600 text-sm">Botoksun ne olduğu, nasıl çalıştığı ve uygulama süreci hakkında detaylı bilgiler.</p>
            </a>
            <a
              href="/tr/blog/botoks-etkisinin-suresi-ne-kadar-kalicidir"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Botoks Etkisinin Süresi</h4>
              <p className="text-gray-600 text-sm">Botoksun ne kadar süre etkili olduğu ve kalıcılık faktörleri.</p>
            </a>
            <a
              href="/tr/blog/orijinal-botoks-anlama-yontemleri-guvenli-urun-tespit-etme"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Orijinal Botoksu Anlama Yöntemleri</h4>
              <p className="text-gray-600 text-sm">Orijinal botoks nasıl anlaşılır ve sahte ürünlerden nasıl korunulur?</p>
            </a>
            <div className="bg-white rounded-2xl shadow-lg p-6 text-center">
              <p className="text-gray-600 mb-4">
                Daha fazla bilgilendirici içerik için bizi takip edin.
              </p>
              <a
                href="/tr/blog"
                className="inline-flex items-center px-4 py-2 bg-pink-600 text-white rounded-full hover:bg-pink-700 transition-colors text-sm"
              >
                Tüm Blog Yazılarını Görüntüle
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
